import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Plus,
  Search,
  Play,
  Pause,
  Copy,
  Trash2,
  MoreVertical,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Eye,
  FileCheck,
  Send,
  GitBranch,
  User,
  Calendar,
  ShieldCheck,
  Ban,
  Share2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { useNotifications } from '../contexts/NotificationContext';
import { useBonus } from '../contexts/BonusContext';
import { WorkflowShareModal, type ShareableWorkflow } from './WorkflowShareModal';

interface Workflow {
  id: string;
  name: string;
  description: string;
  status: 'draft' | 'pending_review' | 'approved' | 'rejected' | 'in_production' | 'paused';
  createdBy: string;
  createdAt: string;
  lastRun?: string;
  executions: number;
  nodeCount: number;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewComments?: string;
  version: string;
  // Métricas de tiempo de desarrollo
  timeSpent: number; // en horas
  daysWorked: number;
  testsRun: number;
  changesCount: number;
}

const mockWorkflows: Workflow[] = [
  {
    id: '1',
    name: 'Envío Automático de Ofertas',
    description: 'Envía ofertas personalizadas a clientes cada lunes',
    status: 'in_production',
    createdBy: 'Juan Pérez',
    createdAt: '2024-01-15',
    lastRun: '2024-02-05 09:00',
    executions: 24,
    nodeCount: 8,
    reviewedBy: 'Admin Carlos',
    reviewedAt: '2024-01-16',
    reviewComments: 'Workflow aprobado. Excelente implementación.',
    version: 'v1.2',
    timeSpent: 10,
    daysWorked: 5,
    testsRun: 15,
    changesCount: 3,
  },
  {
    id: '2',
    name: 'Notificación de Nuevos Activos',
    description: 'Notifica al equipo cuando se registra un nuevo activo',
    status: 'pending_review',
    createdBy: 'María González',
    createdAt: '2024-02-03',
    executions: 0,
    nodeCount: 6,
    version: 'v1.0',
    timeSpent: 5,
    daysWorked: 3,
    testsRun: 10,
    changesCount: 2,
  },
  {
    id: '3',
    name: 'Análisis de Sentimiento en Emails',
    description: 'Analiza emails entrantes y categoriza por prioridad',
    status: 'pending_review',
    createdBy: 'Pedro Ramírez',
    createdAt: '2024-02-04',
    executions: 0,
    nodeCount: 12,
    version: 'v1.0',
    timeSpent: 8,
    daysWorked: 4,
    testsRun: 12,
    changesCount: 1,
  },
  {
    id: '4',
    name: 'Generación de Reportes Semanales',
    description: 'Genera y envía reportes de ventas cada viernes',
    status: 'approved',
    createdBy: 'Ana López',
    createdAt: '2024-01-28',
    lastRun: '2024-02-02 17:00',
    executions: 2,
    nodeCount: 10,
    reviewedBy: 'Admin Carlos',
    reviewedAt: '2024-01-29',
    reviewComments: 'Aprobado para deploy en producción.',
    version: 'v1.1',
    timeSpent: 6,
    daysWorked: 3,
    testsRun: 8,
    changesCount: 0,
  },
  {
    id: '5',
    name: 'Backup de Base de Datos',
    description: 'Realiza backup automático de la base de datos',
    status: 'in_production',
    createdBy: 'Carlos Mendoza',
    createdAt: '2024-01-10',
    lastRun: '2024-02-05 03:00',
    executions: 78,
    nodeCount: 5,
    reviewedBy: 'Admin Carlos',
    reviewedAt: '2024-01-11',
    version: 'v2.0',
    timeSpent: 12,
    daysWorked: 6,
    testsRun: 20,
    changesCount: 4,
  },
  {
    id: '6',
    name: 'Sincronización con Shopify',
    description: 'Sincroniza productos e inventario con tienda Shopify',
    status: 'rejected',
    createdBy: 'Luis Torres',
    createdAt: '2024-02-01',
    executions: 0,
    nodeCount: 15,
    reviewedBy: 'Admin Carlos',
    reviewedAt: '2024-02-02',
    reviewComments: 'Rechazado: Falta manejo de errores en el nodo de API. Por favor revisa y vuelve a enviar.',
    version: 'v1.0',
    timeSpent: 7,
    daysWorked: 4,
    testsRun: 14,
    changesCount: 2,
  },
  {
    id: '7',
    name: 'Chatbot de Atención al Cliente',
    description: 'Bot que responde preguntas frecuentes automáticamente',
    status: 'draft',
    createdBy: 'Sofía Ruiz',
    createdAt: '2024-02-05',
    executions: 0,
    nodeCount: 18,
    version: 'v1.0',
    timeSpent: 9,
    daysWorked: 5,
    testsRun: 16,
    changesCount: 3,
  },
  {
    id: '8',
    name: 'Alerta de Certificaciones Vencidas',
    description: 'Envía alertas cuando las certificaciones están por vencer',
    status: 'paused',
    createdBy: 'Diego Morales',
    createdAt: '2024-01-20',
    lastRun: '2024-01-30 08:00',
    executions: 15,
    nodeCount: 7,
    reviewedBy: 'Admin Carlos',
    reviewedAt: '2024-01-21',
    version: 'v1.0',
    timeSpent: 4,
    daysWorked: 2,
    testsRun: 6,
    changesCount: 1,
  },
];

export function WorkflowsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN' || (user?.role as string) === 'admin';
  const { sendWorkflowApproval, sendWorkflowRejection } = useNotifications();
  const { awardBonus } = useBonus();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [workflows, setWorkflows] = useState(mockWorkflows);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [reviewComments, setReviewComments] = useState('');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [bonusAmount, setBonusAmount] = useState(500); // Bonificación por defecto
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [workflowToShare, setWorkflowToShare] = useState<ShareableWorkflow | null>(null);

  const handleShare = (workflow: Workflow) => {
    setWorkflowToShare({
      id: workflow.id,
      name: workflow.name,
      description: workflow.description,
      status: workflow.status,
      nodeCount: workflow.nodeCount,
      executions: workflow.executions,
      timeSpent: workflow.timeSpent,
      createdBy: workflow.createdBy,
    });
    setShareModalOpen(true);
  };

  const filteredWorkflows = workflows.filter(w =>
    w.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.createdBy.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getWorkflowsByStatus = (status: string) => {
    if (status === 'all') return filteredWorkflows;
    return filteredWorkflows.filter(w => w.status === status);
  };

  const pendingCount = workflows.filter(w => w.status === 'pending_review').length;
  const productionCount = workflows.filter(w => w.status === 'in_production').length;
  const draftCount = workflows.filter(w => w.status === 'draft').length;

  const getStatusBadge = (status: Workflow['status']) => {
    const configs = {
      draft: { label: 'Borrador', variant: 'secondary' as const, icon: Clock },
      pending_review: { label: 'Pendiente Revisión', variant: 'default' as const, icon: AlertCircle },
      approved: { label: 'Aprobado', variant: 'default' as const, icon: CheckCircle2 },
      rejected: { label: 'Rechazado', variant: 'destructive' as const, icon: XCircle },
      in_production: { label: 'En Producción', variant: 'default' as const, icon: CheckCircle2 },
      paused: { label: 'Pausado', variant: 'secondary' as const, icon: Pause },
    };

    const config = configs[status];
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const handleApprove = async (workflow: Workflow) => {
    if (!reviewComments.trim()) {
      toast.error('Por favor agrega comentarios de revisión');
      return;
    }

    toast.info('Aprobando workflow y generando bonificación...');
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Crear bonificación
    const bonus = awardBonus({
      employeeId: `emp_${workflow.createdBy.toLowerCase().replace(/ /g, '_')}`,
      employeeName: workflow.createdBy,
      workflowId: workflow.id,
      workflowName: workflow.name,
      amount: bonusAmount,
      awardedBy: user?.name || 'Admin',
      reason: `Aprobación de workflow: ${reviewComments}`,
    });

    // Enviar notificación al empleado
    sendWorkflowApproval(
      workflow.createdBy,
      workflow.name,
      workflow.id,
      bonusAmount,
      bonus.id
    );

    // Actualizar workflow
    setWorkflows(workflows.map(w =>
      w.id === workflow.id
        ? {
            ...w,
            status: 'approved',
            reviewedBy: user?.name || 'Admin',
            reviewedAt: new Date().toISOString(),
            reviewComments: reviewComments,
          }
        : w
    ));

    setReviewDialogOpen(false);
    setReviewComments('');
    
    toast.success(`✅ Workflow aprobado`);
    toast.success(`💰 Bonificación de $${bonusAmount} USD otorgada a ${workflow.createdBy}`, {
      duration: 5000,
    });
  };

  const handleReject = async (workflow: Workflow) => {
    if (!reviewComments.trim()) {
      toast.error('Por favor explica el motivo del rechazo');
      return;
    }

    toast.info('Rechazando workflow...');
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Enviar notificación de rechazo
    sendWorkflowRejection(
      workflow.createdBy,
      workflow.name,
      workflow.id,
      reviewComments
    );

    // Actualizar workflow
    setWorkflows(workflows.map(w =>
      w.id === workflow.id
        ? {
            ...w,
            status: 'rejected',
            reviewedBy: user?.name || 'Admin',
            reviewedAt: new Date().toISOString(),
            reviewComments: reviewComments,
          }
        : w
    ));

    setReviewDialogOpen(false);
    setReviewComments('');
    toast.success(`Workflow "${workflow.name}" rechazado`);
  };

  const handleDeploy = async (workflow: Workflow) => {
    toast.info('Desplegando a producción...');
    await new Promise(resolve => setTimeout(resolve, 1500));

    setWorkflows(workflows.map(w =>
      w.id === workflow.id
        ? { ...w, status: 'in_production' }
        : w
    ));

    toast.success(`Workflow "${workflow.name}" desplegado en producción 🚀`);
  };

  const handlePause = async (id: string) => {
    const workflow = workflows.find(w => w.id === id);
    toast.info(`Pausando workflow...`);
    await new Promise(resolve => setTimeout(resolve, 800));
    
    setWorkflows(workflows.map(w =>
      w.id === id ? { ...w, status: 'paused' as const } : w
    ));
    
    toast.success(`Workflow "${workflow?.name}" pausado`);
  };

  const handleResume = async (id: string) => {
    const workflow = workflows.find(w => w.id === id);
    toast.info(`Reanudando workflow...`);
    await new Promise(resolve => setTimeout(resolve, 800));
    
    setWorkflows(workflows.map(w =>
      w.id === id ? { ...w, status: 'in_production' as const } : w
    ));
    
    toast.success(`Workflow "${workflow?.name}" reanudado`);
  };

  const handleDelete = async (id: string) => {
    const workflow = workflows.find(w => w.id === id);
    toast.info('Eliminando workflow...');
    await new Promise(resolve => setTimeout(resolve, 800));
    
    setWorkflows(workflows.filter(w => w.id !== id));
    toast.success(`Workflow "${workflow?.name}" eliminado`);
  };

  const handleSubmitForReview = async (id: string) => {
    const workflow = workflows.find(w => w.id === id);
    toast.info('Enviando a revisión...');
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setWorkflows(workflows.map(w =>
      w.id === id ? { ...w, status: 'pending_review' as const } : w
    ));
    
    toast.success(`Workflow "${workflow?.name}" enviado para revisión`);
  };

  const openReviewDialog = (workflow: Workflow) => {
    setSelectedWorkflow(workflow);
    setReviewComments('');
    setReviewDialogOpen(true);
  };

  const openDetailsDialog = (workflow: Workflow) => {
    setSelectedWorkflow(workflow);
    setDetailsDialogOpen(true);
  };

  return (
    <div className="space-y-8 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl mb-2">Workflows</h1>
          <p className="text-muted-foreground">
            {isAdmin 
              ? 'Revisa y aprueba workflows creados por empleados'
              : 'Gestiona tus automatizaciones personalizadas'
            }
          </p>
        </div>
        <Button 
          onClick={() => navigate('/dashboard/workflows/new')}
          className="bg-primary hover:bg-primary/90"
        >
          <Plus className="mr-2 h-4 w-4" />
          Crear Workflow
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-6 md:grid-cols-4">
        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-blue-500"></div>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              En Producción
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{productionCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Workflows activos
            </p>
          </CardContent>
        </Card>

        {isAdmin && (
          <Card className="relative overflow-hidden border-border">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-orange-500"></div>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pendientes de Revisión
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{pendingCount}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Requieren aprobación
              </p>
            </CardContent>
          </Card>
        )}

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gray-500"></div>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Borradores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{draftCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              En desarrollo
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total de Ejecuciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {workflows.reduce((sum, w) => sum + w.executions, 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Este mes
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar workflows por nombre, descripción o creador..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 h-11"
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue={isAdmin ? "pending_review" : "all"} className="w-full">
        <TabsList className={isAdmin ? "grid w-full grid-cols-6" : "grid w-full grid-cols-4"}>
          <TabsTrigger value="all">Todos</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="pending_review" className="relative">
              Pendientes
              {pendingCount > 0 && (
                <Badge className="ml-2 h-5 w-5 flex items-center justify-center p-0 rounded-full bg-orange-500">
                  {pendingCount}
                </Badge>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="in_production">Producción</TabsTrigger>
          <TabsTrigger value="approved">Aprobados</TabsTrigger>
          <TabsTrigger value="draft">Borradores</TabsTrigger>
          {isAdmin && <TabsTrigger value="rejected">Rechazados</TabsTrigger>}
        </TabsList>

        {['all', 'pending_review', 'in_production', 'approved', 'draft', 'rejected'].map(tabValue => (
          <TabsContent key={tabValue} value={tabValue} className="space-y-4 mt-6">
            {getWorkflowsByStatus(tabValue).length === 0 ? (
              <Card className="relative overflow-hidden border-border">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
                <CardContent className="py-12">
                  <div className="text-center text-muted-foreground">
                    <GitBranch className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No hay workflows en esta categoría</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              getWorkflowsByStatus(tabValue).map((workflow) => (
                <Card key={workflow.id} className="relative overflow-hidden border-border hover:shadow-lg transition-shadow">
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-start gap-4">
                          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 flex-shrink-0">
                            <GitBranch className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-3">
                              <h3 className="text-lg font-semibold">{workflow.name}</h3>
                              {getStatusBadge(workflow.status)}
                              <Badge variant="outline" className="gap-1">
                                {workflow.nodeCount} nodos
                              </Badge>
                              <Badge variant="outline">{workflow.version}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {workflow.description}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                Creado por {workflow.createdBy}
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(workflow.createdAt).toLocaleDateString('es-MX')}
                              </div>
                              {workflow.lastRun && (
                                <div className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  Última ejecución: {workflow.lastRun}
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <Play className="h-3 w-3" />
                                {workflow.executions} ejecuciones
                              </div>
                            </div>
                            {workflow.reviewComments && (
                              <div className="mt-3 p-3 bg-accent/50 rounded-lg">
                                <div className="flex items-center gap-2 mb-1">
                                  <ShieldCheck className="h-4 w-4 text-primary" />
                                  <span className="text-xs font-medium">
                                    Revisado por {workflow.reviewedBy}
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {workflow.reviewComments}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Admin Actions for Pending Review */}
                        {isAdmin && workflow.status === 'pending_review' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDetailsDialog(workflow)}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              Ver Detalles
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => openReviewDialog(workflow)}
                              className="bg-primary hover:bg-primary/90"
                            >
                              <FileCheck className="mr-2 h-4 w-4" />
                              Revisar
                            </Button>
                          </>
                        )}

                        {/* Admin Actions for Approved */}
                        {isAdmin && workflow.status === 'approved' && (
                          <Button
                            size="sm"
                            onClick={() => handleDeploy(workflow)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Send className="mr-2 h-4 w-4" />
                            Desplegar a Producción
                          </Button>
                        )}

                        {/* Employee Actions for Draft */}
                        {!isAdmin && workflow.status === 'draft' && (
                          <Button
                            size="sm"
                            onClick={() => handleSubmitForReview(workflow.id)}
                            className="bg-primary hover:bg-primary/90"
                          >
                            <Send className="mr-2 h-4 w-4" />
                              Enviar a Revisión
                          </Button>
                        )}

                        {/* Production Controls */}
                        {workflow.status === 'in_production' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePause(workflow.id)}
                          >
                            <Pause className="mr-2 h-4 w-4" />
                            Pausar
                          </Button>
                        )}

                        {workflow.status === 'paused' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResume(workflow.id)}
                          >
                            <Play className="mr-2 h-4 w-4" />
                            Reanudar
                          </Button>
                        )}

                        {/* More Options */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/dashboard/workflows/${workflow.id}`)}>
                              <Eye className="mr-2 h-4 w-4" />
                              Ver detalles
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/dashboard/workflows/${workflow.id}`)}>
                              <Copy className="mr-2 h-4 w-4" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleShare(workflow)}>
                              <Share2 className="mr-2 h-4 w-4 text-primary" />
                              <span className="text-primary font-medium">Compartir en redes</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDelete(workflow.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Revisar Workflow</DialogTitle>
            <DialogDescription>
              Revisa el workflow "{selectedWorkflow?.name}" y decide si aprobarlo o rechazarlo
            </DialogDescription>
          </DialogHeader>

          {selectedWorkflow && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-accent/50 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Creado por</p>
                  <p className="text-sm font-medium">{selectedWorkflow.createdBy}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Fecha de creación</p>
                  <p className="text-sm font-medium">
                    {new Date(selectedWorkflow.createdAt).toLocaleDateString('es-MX')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Número de nodos</p>
                  <p className="text-sm font-medium">{selectedWorkflow.nodeCount} nodos</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Versión</p>
                  <p className="text-sm font-medium">{selectedWorkflow.version}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="review-comments">Comentarios de Revisión *</Label>
                <Textarea
                  id="review-comments"
                  placeholder="Agrega tus comentarios sobre este workflow (requerido)..."
                  value={reviewComments}
                  onChange={(e) => setReviewComments(e.target.value)}
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  Estos comentarios serán visibles para el creador del workflow
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setReviewDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedWorkflow && handleReject(selectedWorkflow)}
            >
              <Ban className="mr-2 h-4 w-4" />
              Rechazar
            </Button>
            <Button
              onClick={() => selectedWorkflow && handleApprove(selectedWorkflow)}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Aprobar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Detalles del Workflow</DialogTitle>
            <DialogDescription>
              Información completa sobre "{selectedWorkflow?.name}"
            </DialogDescription>
          </DialogHeader>

          {selectedWorkflow && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-4 pr-4">
                <div>
                  <h3 className="font-medium mb-2">Información General</h3>
                  <div className="grid grid-cols-2 gap-3 p-4 bg-accent/50 rounded-lg">
                    <div>
                      <p className="text-xs text-muted-foreground">Estado</p>
                      <div className="mt-1">{getStatusBadge(selectedWorkflow.status)}</div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Versión</p>
                      <p className="text-sm font-medium mt-1">{selectedWorkflow.version}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Nodos</p>
                      <p className="text-sm font-medium mt-1">{selectedWorkflow.nodeCount} nodos</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Ejecuciones</p>
                      <p className="text-sm font-medium mt-1">{selectedWorkflow.executions}</p>
                    </div>
                  </div>
                </div>

                {isAdmin && (
                  <div>
                    <h3 className="font-medium mb-2">⏱️ Métricas de Desarrollo</h3>
                    <div className="grid grid-cols-2 gap-3 p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg border border-primary/20">
                      <div>
                        <p className="text-xs text-muted-foreground">Tiempo invertido</p>
                        <p className="text-lg font-bold mt-1">{selectedWorkflow.timeSpent}h</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Días trabajados</p>
                        <p className="text-lg font-bold mt-1">{selectedWorkflow.daysWorked} días</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Pruebas ejecutadas</p>
                        <p className="text-lg font-bold mt-1">{selectedWorkflow.testsRun} tests</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Iteraciones</p>
                        <p className="text-lg font-bold mt-1">{selectedWorkflow.changesCount} cambios</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      💡 El empleado {selectedWorkflow.createdBy} trabajó aproximadamente {(selectedWorkflow.timeSpent / selectedWorkflow.daysWorked).toFixed(1)}h por día en este workflow
                    </p>
                  </div>
                )}

                <div>
                  <h3 className="font-medium mb-2">Descripción</h3>
                  <p className="text-sm text-muted-foreground p-4 bg-accent/50 rounded-lg">
                    {selectedWorkflow.description}
                  </p>
                </div>

                <div>
                  <h3 className="font-medium mb-2">Diagrama del Workflow</h3>
                  <div className="p-8 bg-accent/50 rounded-lg text-center">
                    <GitBranch className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                    <p className="text-sm text-muted-foreground">
                      Vista previa del diagrama del workflow con {selectedWorkflow.nodeCount} nodos
                    </p>
                  </div>
                </div>
              </div>
            </ScrollArea>
          )}

          <DialogFooter>
            <Button onClick={() => setDetailsDialogOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <WorkflowShareModal
        workflow={workflowToShare}
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
      />
    </div>
  );
}